import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, Col, Container, Form, Row, InputGroup } from "react-bootstrap";
import { commonService } from "../../../utils/commonService";
import { errorToastMessage, infoToastMessage, warnToastMessage } from "../../../utils/toastMessage";

const WINDOW_TABS_CONFIG = [
    { id: "0-13", key: "weightage0to13" },
    { id: "14-29", key: "weightage14to29" },
    { id: "30-59", key: "weightage30to59" },
    { id: "60+", key: "weightage60plus" },
];

const CATEGORY_COLORS = {
    A: "#4A9EF5",
    B: "#A87CF5",
    C: "#3ECFCF",
};

const BookingWindow = ({ data = [], onUpdate }) => {
    const availableTabs = useMemo(
        () => WINDOW_TABS_CONFIG.filter((tab) => data.some((item) => item[tab.key] !== undefined)),
        [data],
    );

    const [activeWindow, setActiveWindow] = useState(availableTabs[0]?.id || null);

    useEffect(() => {
        if (!availableTabs.length) {
            setActiveWindow(null);
            return;
        }
        if (!availableTabs.find((tab) => tab.id === activeWindow)) {
            setActiveWindow(availableTabs[0].id);
        }
    }, [availableTabs, activeWindow]);

    const activeTab = availableTabs.find((tab) => tab.id === activeWindow);

    const totalForWindow = useMemo(() => {
        if (!activeTab) return 0;
        return data.reduce((sum, item) => sum + (Number(item[activeTab.key]) || 0), 0);
    }, [activeTab, data]);

    const isBalanced = Math.abs(totalForWindow - 100) < 0.5;
    const isOverLimit = totalForWindow > 100;

    const handleChange = (index, newValue) => {
        if (!activeTab) return;

        const bounded = Math.max(0, Math.min(100, Number(newValue)));
        onUpdate?.(index, activeTab.key, bounded);
    };

    return (
        <div className="booking-window-container booking-tab mt-3" style={{ overflow: "visible" }}>
            <Container fluid style={{ overflow: "visible" }}>
                <Row className="mb-3">
                    <Col lg={12}>
                        <div className="info-section mb-0">
                            <p className="info-text">
                                <b className="info-title">Dynamic Weight Profiles : </b>
                                KPI weights shift by lead time. Close-in dates emphasize Pickup/OTB. Far-out dates rely more on STLY, Events, and Market Intelligence.
                            </p>
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <div className="d-flex flex-wrap gap-2">
                            <div className="custom-range-picker gap-2 me-2 mb-3">
                                {availableTabs.map((tab) => (
                                    <button id={commonService.generateGlobalClickTrackerID("BookingWindow", `${tab.id}-days`)}
                                        key={tab.id}
                                        type="button"
                                        className={`preset-btn text-center ${activeWindow === tab.id ? "active" : ""}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setActiveWindow(tab.id);
                                        }}
                                    >
                                        {tab.id} days
                                    </button>
                                ))}
                            </div>
                        </div>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col>
                        <div
                            className={`p-2 rounded border ${isOverLimit
                                ? "bg-danger-subtle border-danger-subtle"
                                : isBalanced
                                    ? "bg-success-subtle border-success-subtle"
                                    : "bg-warning-subtle border-warning-subtle"
                                }`}
                        >
                            <strong>
                                {isOverLimit
                                    ? "ERROR"
                                    : isBalanced
                                        ? "OK"
                                        : "WARN"}{" "}
                                Window Total: {totalForWindow.toFixed(1)}% - {data.length} KPIs
                            </strong>
                        </div>
                    </Col>
                </Row>

                <div style={{ maxHeight: "500px", overflowY: "auto", overflowX: "hidden" }}>
                    <Row>
                        <Col>
                            {!activeTab ? (
                                <Card className="mb-2 shadow-sm border-0">
                                    <Card.Body>
                                        <p className="text-muted">No booking window data available</p>
                                    </Card.Body>
                                </Card>
                            ) : (
                                data.map((item, index) => {
                                    const value = Number(item[activeTab.key]) || 0;
                                    const catColor = commonService.CreateColor(item.category).BGColor;
                                    return (
                                        <Card key={`${item.kpi}-${activeTab.id}-${index}`} className="mb-2" style={{ background: "none" }} >
                                            <Card.Body className="p-2">
                                                <div className="d-flex align-items-center gap-2">
                                                    <div style={{ minWidth: "60%", fontSize: "14px" }}>{item.kpi}</div>
                                                    <Form.Range id={commonService.generateGlobalClickTrackerID("BookingWindow", `${item.kpi}-form-range-onchange`, item.kpi)}
                                                        className="ds-range-input dynamic-range"
                                                        min={0}
                                                        max={100}
                                                        step={1}
                                                        value={value}
                                                        onChange={(e) => handleChange(index, e.target.value)}
                                                        style={{ height: "8px", flex: 1, "--range-color": catColor }}
                                                    />
                                                    <div className="" style={{ color: catColor, fontWeight: "bold" }}>
                                                        {value}%
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    );
                                })
                            )}
                        </Col>
                    </Row>
                </div>
            </Container>
        </div>
    );
};

export default BookingWindow;
